import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Copy, Check, Trash2, Clock, Eye, Lock, ArrowRight, Shield, QrCode as QrIcon } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getPushPreview, deletePush } from '../lib/api';
import { useBrand } from '../context/BrandContext';
import { toast } from 'sonner';

export default function PushCreated() {
  const { token, slug: urlSlug } = useParams();
  const ctx = useBrand();
  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState(false);

  // Public share URL should use custom domain when available (for verified brand)
  const brandFromMeta = meta?.brand;
  const useCustom = brandFromMeta?.custom_domain && brandFromMeta?.domain_verified;
  const shareUrl = useCustom
    ? `https://${brandFromMeta.custom_domain}/p/${token}`
    : urlSlug
      ? `${window.location.origin}/b/${urlSlug}/p/${token}`
      : ctx.mode === 'branded-host'
        ? `${window.location.origin}/p/${token}`
        : `${window.location.origin}/p/${token}`;
  const basePath = ctx.mode === 'branded-host' ? '' : (urlSlug ? `/b/${urlSlug}` : '');

  useEffect(() => {
    getPushPreview(token).then(setMeta).catch(() => setMeta(null));
  }, [token]);

  const brand = meta?.brand;
  const primary = brand?.primary_color || '#0d63b0';
  const accent = brand?.accent_color || '#1e88d6';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiato');
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error('Impossibile copiare'); }
  };

  const doDelete = async () => {
    if (!confirm('Eliminare definitivamente questo push?')) return;
    try { await deletePush(token); toast.success('Push eliminato'); }
    catch (e) { toast.error('Errore'); }
  };

  const qrColor = (primary || '#0d63b0').replace('#', '');
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(shareUrl)}&margin=8&color=${qrColor}`;

  return (
    <div className="py-12" style={{ background: `linear-gradient(180deg, ${accent}12, #ffffff 60%)` }}>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700">
            <Check className="h-7 w-7" strokeWidth={2.6} />
          </div>
          <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">Push creato con successo</h1>
          <p className="mt-2 text-slate-600">Condividi il link qui sotto. Verrà eliminato automaticamente dopo la scadenza.</p>
          {brand && <div className="mt-2 text-xs text-slate-500">Brand: <span className="font-semibold" style={{ color: primary }}>{brand.name}</span></div>}
        </div>

        <Card className="mt-8 border border-slate-200 p-6">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Il tuo link sicuro</div>
          <div className="mt-3 flex flex-col md:flex-row gap-2">
            <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 font-mono text-sm text-slate-800 break-all">
              {shareUrl}
            </div>
            <Button onClick={copy} className="text-white shrink-0" style={{ backgroundColor: primary }}>
              {copied ? (<><Check className="h-4 w-4 mr-1.5" /> Copiato</>) : (<><Copy className="h-4 w-4 mr-1.5" /> Copia link</>)}
            </Button>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Impostazioni</div>
              {meta ? (
                <ul className="mt-3 space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-slate-700"><Clock className="h-4 w-4" style={{ color: primary }} /> Scade tra <strong className="text-slate-900">{meta.days_left}</strong> {meta.days_left === 1 ? 'giorno' : 'giorni'}</li>
                  <li className="flex items-center gap-2 text-slate-700"><Eye className="h-4 w-4" style={{ color: primary }} /> Rimanenti <strong className="text-slate-900">{meta.views_left}</strong> visualizzazioni</li>
                  <li className="flex items-center gap-2 text-slate-700"><Lock className="h-4 w-4" style={{ color: primary }} /> Protetto: <strong className="text-slate-900">{meta.requires_passphrase ? 'passphrase attiva' : 'no'}</strong></li>
                  <li className="flex items-center gap-2 text-slate-700"><Shield className="h-4 w-4" style={{ color: primary }} /> Tipo: <strong className="text-slate-900 capitalize">{meta.kind === 'text' ? 'testo' : meta.kind === 'files' ? 'file' : 'url'}</strong></li>
                </ul>
              ) : <div className="text-sm text-slate-500 mt-3">Caricamento...</div>}
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><QrIcon className="h-3.5 w-3.5" /> QR Code</div>
              <img src={qrSrc} alt="QR" className="rounded-lg border border-slate-200 bg-white" />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to={basePath ? `${basePath}/p/new` : '/p/new'}>Crea un altro push <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={basePath || '/dashboard'}>{basePath ? 'Landing brand' : 'Vai alla dashboard'}</Link>
            </Button>
            <Button onClick={doDelete} variant="outline" className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300">
              <Trash2 className="h-4 w-4 mr-1.5" /> Distruggi ora
            </Button>
          </div>
        </Card>

        <p className="mt-6 text-center text-xs text-slate-500">Suggerimento: invia il link e l'eventuale passphrase su canali diversi (es. email + SMS).</p>
      </div>
    </div>
  );
}
